import { useState } from "react";
import { Button, Input, Tag, Tooltip, Popconfirm, message } from "antd";
import { Plus, Phone, Check, X, Pencil, Trash2, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CustomerPhoneEntry } from "../../api/ChannelCustomer";

/**
 * Quản lý số điện thoại phụ của khách (param `phones` của API khách hàng).
 * Mỗi số gồm: phone (bắt buộc), note (ghi chú), is_primary (đánh dấu số chính).
 * Component chỉ dựng danh sách rồi trả cả mảng về cha lưu một lượt — backend
 * nhận nguyên mảng chứ không có API thêm/xóa từng số.
 */
interface Props {
    phones: CustomerPhoneEntry[];
    /** Số chính đang lưu ở trường phone của khách, dùng để chặn trùng */
    mainPhone?: string | null;
    onSave: (phones: CustomerPhoneEntry[]) => Promise<void> | void;
}

const onlyDigits = (raw: string) => raw.replace(/[^\d]/g, "");

export default function CustomerPhones({ phones, mainPhone, onSave }: Props) {
    const { t } = useTranslation();
    // index đang sửa; -1 = đang thêm mới; null = không sửa gì
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [draftPhone, setDraftPhone] = useState("");
    const [draftNote, setDraftNote] = useState("");
    const [saving, setSaving] = useState(false);

    const list = Array.isArray(phones) ? phones : [];

    const resetDraft = () => {
        setEditingIndex(null);
        setDraftPhone("");
        setDraftNote("");
    };

    const startAdd = () => {
        setEditingIndex(-1);
        setDraftPhone("");
        setDraftNote("");
    };

    const startEdit = (index: number) => {
        setEditingIndex(index);
        setDraftPhone(list[index]?.phone || "");
        setDraftNote(list[index]?.note || "");
    };

    const commit = async (next: CustomerPhoneEntry[]) => {
        setSaving(true);
        try {
            await onSave(next);
            resetDraft();
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDraft = async () => {
        const phone = onlyDigits(draftPhone);
        if (!phone) {
            message.warning(t("cd.phone_required") || "Vui lòng nhập số điện thoại");
            return;
        }
        // Trùng số chính hoặc trùng số phụ khác -> chặn cho khỏi lưu 2 lần cùng số
        const duplicated = (mainPhone && onlyDigits(mainPhone) === phone)
            || list.some((p, i) => i !== editingIndex && onlyDigits(p.phone || "") === phone);
        if (duplicated) {
            message.warning(t("cd.phone_duplicated") || "Số điện thoại này đã có trong hồ sơ khách");
            return;
        }

        const entry: CustomerPhoneEntry = { phone, note: draftNote.trim() || null };
        const next = editingIndex === -1
            ? [...list, entry]
            : list.map((p, i) => (i === editingIndex ? { ...p, ...entry } : p));
        await commit(next);
    };

    const handleDelete = async (index: number) => {
        await commit(list.filter((_, i) => i !== index));
    };

    // Đánh dấu số chính: chỉ một số được là chính
    const handleSetPrimary = async (index: number) => {
        await commit(list.map((p, i) => ({ ...p, is_primary: i === index ? 1 : 0 })));
    };

    const isEditingRow = (index: number) => editingIndex === index;

    return (
        <div className="py-3 border-b border-gray-100 dark:border-[#303030]">
            <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                    {t("cd.secondary_phones") || "Số điện thoại phụ"} ({list.length})
                </span>
                {editingIndex === null && (
                    <Button
                        size="small"
                        icon={<Plus size={12} />}
                        className="flex items-center text-[12px]"
                        onClick={startAdd}
                    >
                        {t("cd.add_phone") || "Thêm số"}
                    </Button>
                )}
            </div>

            {list.length === 0 && editingIndex === null && (
                <div className="text-gray-400 text-sm">
                    {t("cd.no_secondary_phone") || "Chưa có số phụ"}
                </div>
            )}

            <div className="space-y-2">
                {list.map((p, index) => (
                    isEditingRow(index) ? (
                        <div key={index} className="flex flex-col gap-2">
                            <Input
                                autoFocus
                                value={draftPhone}
                                onChange={(e) => setDraftPhone(e.target.value)}
                                placeholder={t("cd.phone") || "Số điện thoại"}
                            />
                            <Input
                                value={draftNote}
                                onChange={(e) => setDraftNote(e.target.value)}
                                placeholder={t("cd.phone_note") || "Ghi chú (không bắt buộc)"}
                            />
                            <div className="flex justify-end gap-2">
                                <Button size="small" icon={<X size={12} />} onClick={resetDraft} disabled={saving} />
                                <Button size="small" type="primary" icon={<Check size={12} />} loading={saving} onClick={handleSaveDraft} />
                            </div>
                        </div>
                    ) : (
                        <div key={index} className="group flex items-center gap-2">
                            <Phone size={13} className="text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-800 dark:text-gray-100">{p.phone}</span>
                            {!!Number(p.is_primary) && (
                                <Tag color="green" className="mr-0 text-[10px]">
                                    {t("cd.primary_phone") || "Số chính"}
                                </Tag>
                            )}
                            {p.note && (
                                <Tooltip title={p.note}>
                                    <span className="text-[12px] text-gray-400 truncate max-w-[120px]">{p.note}</span>
                                </Tooltip>
                            )}

                            <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {!Number(p.is_primary) && (
                                    <Tooltip title={t("cd.set_primary_phone") || "Đặt làm số chính"}>
                                        <button
                                            className="p-1 rounded text-gray-400 hover:text-[#3e91ab]"
                                            onClick={() => handleSetPrimary(index)}
                                        >
                                            <Star size={13} />
                                        </button>
                                    </Tooltip>
                                )}
                                <Tooltip title={t("cd.edit") || "Sửa"}>
                                    <button
                                        className="p-1 rounded text-gray-400 hover:text-[#3e91ab]"
                                        onClick={() => startEdit(index)}
                                    >
                                        <Pencil size={13} />
                                    </button>
                                </Tooltip>
                                <Popconfirm
                                    title={t("cd.delete_phone") || "Xóa số điện thoại này?"}
                                    okText={t("cd.delete") || "Xóa"}
                                    cancelText={t("cd.cancel") || "Hủy"}
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => handleDelete(index)}
                                >
                                    <button className="p-1 rounded text-gray-400 hover:text-red-500">
                                        <Trash2 size={13} />
                                    </button>
                                </Popconfirm>
                            </div>
                        </div>
                    )
                ))}

                {editingIndex === -1 && (
                    <div className="flex flex-col gap-2">
                        <Input
                            autoFocus
                            value={draftPhone}
                            onChange={(e) => setDraftPhone(e.target.value)}
                            placeholder={t("cd.phone") || "Số điện thoại"}
                        />
                        <Input
                            value={draftNote}
                            onChange={(e) => setDraftNote(e.target.value)}
                            placeholder={t("cd.phone_note") || "Ghi chú (không bắt buộc)"}
                        />
                        <div className="flex justify-end gap-2">
                            <Button size="small" onClick={resetDraft} disabled={saving}>
                                {t("cd.cancel") || "Hủy"}
                            </Button>
                            <Button size="small" type="primary" loading={saving} onClick={handleSaveDraft}>
                                {t("cd.save") || "Lưu"}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
